import { useState, useEffect, lazy, Suspense } from "react";
import HeroSection from "./components/HeroSection";
import MassachusettsCommunitiesWeServe from "./components/CommunitiesWeServe";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const CitiesDirectory = lazy(() => import("./components/CitiesDirectory"));
const StateResources = lazy(() => import("./components/StateResources"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const FinalCTA = lazy(() => import("./components/FinalCTA"));

export default function MassachusettsPage() {
  useEffect(() => {
    document.title =
      "Expedited & Emergency Passport Services Massachusetts | Same-Day MA Service | Fast Passport Center";

    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Fast Passport Center provides emergency, expedited, and same-day passport services across all Massachusetts cities. Serving Boston, Worcester, Springfield, Lowell, Cambridge, and beyond. Same-day courier to the Boston National Passport Center."
      );
    }

    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute(
        "content",
        "expedited passport Massachusetts, emergency passport Massachusetts, same day passport MA, fast passport Boston, passport renewal Worcester, passport service Springfield"
      );
    }

    const geoRegion = document.querySelector('meta[name="geo.region"]');
    if (geoRegion) geoRegion.setAttribute("content", "US-MA");
    const geoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (geoPlacename) geoPlacename.setAttribute("content", "Massachusetts");
    const geoPos = document.querySelector('meta[name="geo.position"]');
    if (geoPos) geoPos.setAttribute("content", "42.4072;-71.3824");
    const icbm = document.querySelector('meta[name="ICBM"]');
    if (icbm) icbm.setAttribute("content", "42.4072, -71.3824");

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical)
      canonical.setAttribute("href", "https://fastpassportcenter.com/massachusetts");

    let schema = document.getElementById("ma-schema");
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "ma-schema";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }

    schema.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://fastpassportcenter.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Massachusetts",
            item: "https://fastpassportcenter.com/massachusetts",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "Fast Passport Center - Massachusetts",
        description:
          "Expedited and emergency passport services across Massachusetts — Boston, Worcester, Springfield, Lowell, and every city in between.",
        url: "https://fastpassportcenter.com/massachusetts",
        telephone: "+1-800-381-3010",
        email: "info@fastpassportcenter.com",
        priceRange: "$$",
        areaServed: {
          "@type": "State",
          name: "Massachusetts",
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "850",
        },
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: "08:00",
            closes: "20:00",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Does Massachusetts have its own passport agency?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Massachusetts does not have a traditional in-person regional passport agency for walk-in emergency appointments. The Boston National Passport Center at 10 Causeway St processes applications by mail. For emergency in-person appointments, Massachusetts residents typically travel to the New York Passport Agency. Most MA travelers find that using an approved courier like Fast Passport Center is significantly faster.",
            },
          },
          {
            "@type": "Question",
            name: "How fast can I get a passport in Massachusetts?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "With Fast Passport Center, Massachusetts residents can get same-day emergency service or 2–3 week expedited processing. Routine service through the post office takes 6–13 weeks plus mailing time.",
            },
          },
          {
            "@type": "Question",
            name: "Which Massachusetts airport should I fly from?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Logan International (BOS) in Boston is Massachusetts's major international airport. For Western MA residents, Bradley International (BDL) in Hartford, CT is often closer. Providence's T.F. Green (PVD) serves Southern MA. Worcester Regional (ORH) serves Central MA with connecting flights.",
            },
          },
        ],
      },
    ]);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <HeroSection />
      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-gray-50">
            <div className="animate-pulse text-gray-400 text-sm">Loading cities...</div>
          </div>
        }
      >
        <CitiesDirectory />
      </Suspense>
      <MassachusettsCommunitiesWeServe />
      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-white">
            <div className="animate-pulse text-gray-400 text-sm">Loading resources...</div>
          </div>
        }
      >
        <StateResources />
      </Suspense>

      <section className="bg-[#F7F8FA] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Passport Services
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              How We Serve Massachusetts
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              From the college corridors of Cambridge to the fishing ports of New Bedford, we help
              Massachusetts residents get their passports fast. The Boston National Passport Center
              processes applications — but appointments fill quickly. We cut through the red tape.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "ri-flight-takeoff-line",
                title: "Emergency Same-Day Service",
                desc: "For Massachusetts travelers with flights within 72 hours. We hand-carry your application to the processing center.",
              },
              {
                icon: "ri-time-line",
                title: "Expedited 2–3 Week Service",
                desc: "Standard expedited processing with overnight shipping and tracking for Massachusetts residents.",
              },
              {
                icon: "ri-file-list-3-line",
                title: "New Passport Applications",
                desc: "First-time applicants in Massachusetts — we handle document prep, photos, and acceptance facility scheduling.",
              },
              {
                icon: "ri-refresh-line",
                title: "Passport Renewals",
                desc: "Massachusetts renewals by mail or courier. We ensure your application is complete to avoid delays.",
              },
              {
                icon: "ri-user-add-line",
                title: "Child & Minor Passports",
                desc: "Massachusetts families with kids under 16 — we guide you through parental consent and documentation.",
              },
              {
                icon: "ri-error-warning-line",
                title: "Lost, Stolen & Damaged",
                desc: "Fast replacement for Massachusetts residents who need a new passport quickly.",
              },
            ].map((svc, i) => (
              <div
                key={i}
                className="bg-white rounded-lg p-7 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-5">
                  <i className={`${svc.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{svc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-gray-50">
            <div className="animate-pulse text-gray-400 text-sm">Loading FAQs...</div>
          </div>
        }
      >
        <FAQSection />
      </Suspense>

      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-white">
            <div className="animate-pulse text-gray-400 text-sm">Loading CTA...</div>
          </div>
        }
      >
        <FinalCTA />
      </Suspense>

      <section className="bg-[#F7F8FA] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <SEOCrossLinks state="MA" showCities={false} />
        </div>
      </section>
    </div>
  );
}